import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useCertificates } from '@/hooks/use-content';
import type { CertificateRow } from '@/types/database';
import SectionEyebrow from './SectionEyebrow';

const CertificateCard = memo(({ certificate }: { certificate: CertificateRow }) => {
  const [isFlipped, setIsFlipped] = useState(false);

  if (!certificate.back_image_url) {
    return (
      <div className="w-full aspect-[1.4/1] shadow-card rounded-lg overflow-hidden">
        <img
          src={certificate.front_image_url}
          alt={certificate.title}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  return (
    <div className="perspective-1000">
      <div
        className={`relative w-full aspect-[1.4/1] cursor-pointer transition-transform duration-700 transform-style-3d ${
          isFlipped ? 'rotate-y-180' : ''
        }`}
        onClick={() => setIsFlipped(!isFlipped)}
      >
        <Card className="absolute inset-0 backface-hidden shadow-card overflow-hidden">
          <img
            src={certificate.front_image_url}
            alt={`${certificate.title} — front`}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
          />
        </Card>

        <Card className="absolute inset-0 backface-hidden rotate-y-180 shadow-card overflow-hidden">
          <img
            src={certificate.back_image_url}
            alt={`${certificate.title} — back`}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
          />
        </Card>
      </div>
    </div>
  );
});
CertificateCard.displayName = 'CertificateCard';

const Certificates = () => {
  const { t } = useTranslation();
  const { data: certificatesData, isLoading } = useCertificates();

  return (
    <section id="certificate" className="py-14 sm:py-20 bg-background">
      <div className="container mx-auto px-4">

        {/* Title */}
        <div className="text-center mb-12">
          <SectionEyebrow index="08" label={t('certificates.eyebrow')} />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('certificates.title')}</h2>
          <p className="text-muted-foreground text-lg">{t('certificates.description')}</p>
        </div>

        {/* Layout */}
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8 items-start">
          {isLoading
            ? Array.from({ length: 4 }).map((_, idx) => <Skeleton key={idx} className="aspect-[1.4/1] rounded-lg" />)
            : certificatesData?.map((certificate) => <CertificateCard key={certificate.id} certificate={certificate} />)}
        </div>
      </div>
    </section>
  );
};

export default Certificates;
